import {
  Button,
  Card,
  Code,
  HStack,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure
} from "@chakra-ui/react"
import ReactJson from "@microlink/react-json-view"
import {
  useBlocksConfig,
  useEditorBlock,
  useEditorDispatch
} from "@pathwright/blocks-engine"
import {
  BlockType,
  BlocksTemplateVariablesType
} from "@pathwright/blocks-engine/src/types"
import {
  CheckIcon,
  CopyIcon,
  EditIcon,
  EnvelopeIcon,
  EyeIcon,
  ToolboxIcon,
  XIcon
} from "@pathwright/pathicons"
// @ts-ignore
import ErrorBoundary from "@pathwright/ui/src/components/error/ErrorBoundary"
import PropTypes from "prop-types"
import { useRef, useState } from "react"
import BlockEditor from "./BlockEditor"
import DeleteBlockPrompt from "./DeleteBlockPrompt"

const createSupportMessage = ({
  errorMessage,
  user,
  school
}: {
  errorMessage: string
  user: BlocksTemplateVariablesType["user"]
  school: BlocksTemplateVariablesType["school"]
}) => {
  const { display_name = "", email = "" } = user || {}
  const { name: schoolName = "" } = school || {}
  return `Hello Pathwright team,

I encountered an error in a block in ${schoolName} at ${window.location.href} and would like your help resolving it.

Error message:
${errorMessage}

Thanks,
${display_name}
Account: ${email}
`
}

const createEmailURL = ({
  errorMessage,
  user,
  school
}: {
  errorMessage: string
  user: any
  school: any
}) => {
  const body = createSupportMessage({ errorMessage, user, school })
  const to = "hello@pathwright.com"
  // const cc = `${display_name} <${email}>`
  const subject = `Block error in ${school.name}`

  return `mailto:${to}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`
}

const ErrorModal = ({
  message,
  isOpen,
  onClose
}: {
  message: string
  isOpen: boolean
  onClose: () => void
}) => {
  const [copied, setCopied] = useState(false)

  return (
    <>
      <Modal isCentered size="lg" isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>
            <HStack>
              <Heading as="h2" mr={4}>
                Error
              </Heading>
              {copied ? (
                <Button
                  textDecoration="none"
                  variant="link"
                  leftIcon={<CheckIcon size="1.2em" />}
                  fontSize="0.8em"
                  disabled
                >
                  Copied!
                </Button>
              ) : (
                <Button
                  textDecoration="none"
                  variant="link"
                  leftIcon={<CopyIcon size="1.2em" />}
                  fontSize="0.8em"
                  onClick={() => {
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                    navigator.clipboard.writeText(message)
                  }}
                >
                  Copy to clipboard
                </Button>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Code children={message} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

const DataModal = ({
  block,
  blockType,
  allowEdit,
  onSave
}: {
  block: BlockType
  blockType: string
  allowEdit: boolean
  onSave: (block: BlockType) => void
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [stateBlock, setStateBlock] = useState(block)
  const [editing, setEditing] = useState(false)
  const [previewBlock, setPreviewBlock] = useState<BlockType | null>(null)

  // Using a ref so we can control the button/events in the JSON editor
  const modalRef = useRef<HTMLDivElement>(null)

  const handleStartEditing = () => {
    const jsonEditButton = modalRef.current?.querySelector(
      ".click-to-edit > span"
    ) as HTMLButtonElement

    if (jsonEditButton) {
      jsonEditButton.click()
    }

    setEditing(true)
  }

  const handleCancelEditing = () => {
    const jsonCancelButton = modalRef.current?.querySelector(
      ".edit-cancel"
    ) as HTMLButtonElement

    if (jsonCancelButton) {
      jsonCancelButton.click()
    }

    setEditing(false)
  }

  const handleSaveEdit = () => {
    const jsonConfirmButton = modalRef.current?.querySelector(
      ".edit-check"
    ) as HTMLButtonElement

    if (jsonConfirmButton) {
      jsonConfirmButton.click()
    }

    setEditing(false)
  }

  const blockEditorProps = {
    block: previewBlock,
    blockIndex: `${block.order}-${new Date().getTime()}`,
    blockType
  }

  return (
    <>
      <Button
        variant="link"
        colorScheme="orange"
        leftIcon={<ToolboxIcon />}
        cursor="pointer"
        bg="transparent"
        _hover={{
          opacity: "0.8",
          backgroundColor: "transparent"
        }}
        onClick={onOpen}
        size="md"
      >
        Fix Block data
      </Button>
      <Modal isCentered size="xl" onClose={onClose} isOpen={isOpen}>
        <ModalOverlay />
        <ModalContent maxH="90vh" overflowY="auto">
          <ModalHeader>Block data</ModalHeader>
          <ModalCloseButton />
          <ModalBody
            sx={{
              ".copy-to-clipboard-container": {
                display: "inline-block !important",
                visibility: "hidden"
              },
              ".react-json-view:hover": {
                ".copy-to-clipboard-container": {
                  visibility: "visible"
                }
              },
              ".copy-to-clipboard-container > span > span": {
                display: "inline-flex"
              },
              ".copy-to-clipboard-container > span > span > span:last-of-type":
                {
                  marginLeft: "0px !important"
                },
              ".variable-row, .variable-value, .variable-value textarea": {
                width: "100%",
                lineHeight: "1.4"
              },
              ".variable-row, .variable-row > span, .variable-value > div": {
                display: "flex"
              },
              ".copy-icon svg": {
                fontSize: "18px !important"
              },
              ".expanded-icon svg, .collapsed-icon svg": {
                fontSize: "16px !important"
              },
              ".click-to-edit-icon, .edit-cancel, .edit-check": {
                display: "none"
              }
            }}
          >
            {previewBlock ? (
              <ErrorBoundary
                catchLoadingChunkFailures={false}
                renderError={(error: Error) => (
                  <VStack>
                    <Heading size="sm" mb={4}>
                      This block still has errors.
                    </Heading>
                    <Code
                      maxW="100%"
                      maxH="300px"
                      overflow="auto"
                      children={JSON.stringify(error, null, 2)}
                    />
                    <Button
                      onClick={() => {
                        setPreviewBlock(null)
                      }}
                    >
                      Try again
                    </Button>
                  </VStack>
                )}
              >
                <Text>The block seems to be working correctly.</Text>
                <Card my={4} maxW="100%">
                  <BlockEditor {...blockEditorProps} />
                </Card>
                <Button
                  onClick={() => {
                    onSave(stateBlock)
                    setPreviewBlock(null)
                    onClose()
                  }}
                  mr={2}
                >
                  Save changes
                </Button>
                <Button
                  onClick={() => {
                    setPreviewBlock(null)
                  }}
                >
                  Keep editing
                </Button>
              </ErrorBoundary>
            ) : (
              <VStack ref={modalRef} align="stretch">
                <Text size="xxs" fontWeight="400" mb={4}>
                  Edit this block's content at your own risk. You will have a
                  chance to preview the block before saving it.
                </Text>
                <HStack spacing={4}>
                  {editing ? (
                    <>
                      <Button
                        variant="link"
                        leftIcon={<CheckIcon size="1.2em" />}
                        onClick={handleSaveEdit}
                      >
                        Save
                      </Button>
                      <Button
                        variant="link"
                        leftIcon={<XIcon size="1.2em" />}
                        onClick={handleCancelEditing}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="link"
                        leftIcon={<EditIcon size="1.2em" />}
                        onClick={handleStartEditing}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="link"
                        leftIcon={<EyeIcon size="1.2em" />}
                        onClick={() => {
                          setPreviewBlock(stateBlock)
                        }}
                      >
                        Preview
                      </Button>
                    </>
                  )}
                </HStack>
                <ReactJson
                  displayDataTypes={false}
                  displayObjectSize={false}
                  quotesOnKeys={false}
                  theme="rjv-default"
                  iconStyle="triangle"
                  name={null}
                  collapsed={1}
                  src={stateBlock.data}
                  enableClipboard={(copy) => {
                    if (navigator?.clipboard?.writeText) {
                      navigator.clipboard.writeText(JSON.stringify(copy?.src))
                    }
                  }}
                  onEdit={
                    allowEdit
                      ? (edit) => {
                          const nextBlock = {
                            ...stateBlock,
                            data: { ...edit.updated_src }
                          }
                          setStateBlock(nextBlock)
                          setPreviewBlock(nextBlock)
                        }
                      : undefined
                  }
                />
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

const BlockErrorHandler = (props: {
  error: Error
  block: BlockType
  allowEdit: boolean
  blockType: string
  onFixedBlock: () => void
}) => {
  const { error, block, allowEdit, blockType, onFixedBlock } = props
  const { isSuperuser, openSupportRequest, templateVariables } =
    useBlocksConfig()

  const { deleteBlockID } = useEditorBlock()

  const { handleBlockDelete, handleCancelBlockDelete, handleBlockChange } =
    useEditorDispatch()

  const deleting = deleteBlockID === block.id

  const errorMessage = error.stack
    ? JSON.stringify(error.stack)
    : JSON.stringify(error)

  const { user, school } = templateVariables || {}

  if (!block?.id || !user || !school) return null

  return (
    <VStack
      px={5}
      py={5}
      border="2px solid"
      borderColor="red.300"
      pos="relative"
    >
      {deleting && (
        <DeleteBlockPrompt
          onCancel={handleCancelBlockDelete}
          onDelete={() => handleBlockDelete?.(block.id)}
        />
      )}
      <VStack maxW="sm">
        <Text my={0}>
          The content in this block caused the following error.
        </Text>
        <Code px={2} my={0} color="gray.500" noOfLines={2}>
          {errorMessage}
        </Code>
      </VStack>
      <VStack>
        <Text mt={4} mb={0}>
          How would you like to proceed?
        </Text>
        <HStack spacing={4}>
          {isSuperuser ? (
            <DataModal
              block={block}
              allowEdit={allowEdit}
              blockType={blockType}
              onSave={(updatedBlock: BlockType) => {
                if (!handleBlockChange) return
                handleBlockChange?.(
                  {
                    ...block.data,
                    ...updatedBlock.data
                  },
                  block.id
                )
                onFixedBlock()
              }}
            />
          ) : (
            <Button
              variant="link"
              colorScheme="gray"
              leftIcon={<EnvelopeIcon />}
              cursor="pointer"
              bg="transparent"
              _hover={{
                opacity: "0.8",
                backgroundColor: "transparent"
              }}
              onClick={(event) => {
                event.preventDefault()
                if (openSupportRequest) {
                  openSupportRequest({
                    message: createSupportMessage({
                      errorMessage: error?.message,
                      user,
                      school
                    }),
                    error: error?.message || ""
                  })
                } else {
                  window.open(
                    createEmailURL({
                      errorMessage: error?.message,
                      user,
                      school
                    })
                  )
                }
              }}
              size="md"
            >
              Send to Pathwright Support
            </Button>
          )}
          <Button
            variant="link"
            colorScheme="red"
            color="rgb(255, 0, 0)"
            bg="transparent"
            _hover={{
              opacity: "0.8",
              backgroundColor: "transparent"
            }}
            leftIcon={<XIcon color="rgb(255, 0, 0)" size="1.2em" />}
            onClick={() => handleBlockDelete?.(block.id)}
            cursor="pointer"
            size="md"
            // px={5}
          >
            Delete block
          </Button>
        </HStack>
      </VStack>
    </VStack>
  )
}

BlockErrorHandler.displayName = "BlockErrorHandler"

BlockErrorHandler.propTypes = {
  error: PropTypes.object.isRequired,
  block: PropTypes.object.isRequired,
  allowEdit: PropTypes.bool
}

export default BlockErrorHandler
